import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { db, auth } from '../config/firebase';
import { logError, log } from '../utils/logger';

export interface AdminUser {
  id: string;
  email: string;
  name: string;
  role: 'owner' | 'super_admin' | 'admin' | 'moderator';
  permissions: AdminPermission[];
  lastLogin: Date;
  isActive: boolean;
  created: Date;
  createdBy?: string; // ID of admin who created this user
  lastModifiedBy?: string; // ID of admin who last modified permissions
  lastModified?: Date;
  ipWhitelist?: string[]; // Optional IP restriction
  requireMFA?: boolean; // Require multi-factor authentication
}

// Enhanced permission system with granular controls
export type AdminModule =
  | 'users' | 'content' | 'reports' | 'locations' | 'subscriptions'
  | 'analytics' | 'settings' | 'financial' | 'payments'
  | 'team_management' | 'system_admin' | 'audit_logs' | 'user_locations';

export type AdminAction = 
  | 'view' | 'create' | 'edit' | 'delete' | 'approve' | 'ban' 
  | 'invite' | 'revoke' | 'export' | 'configure' | 'emergency_access';

export interface AdminPermission {
  module: AdminModule;
  actions: AdminAction[];
  restrictions?: {
    maxUsers?: number; // For team management - max users this admin can manage
    readOnly?: boolean; // View-only access to sensitive data
    requireApproval?: boolean; // Actions require higher-level approval
  };
}

// Permission inheritance hierarchy
export const ROLE_PERMISSIONS: Record<AdminUser['role'], AdminPermission[]> = {
  owner: [
    // Owner has full access to everything including system administration
    { module: 'users', actions: ['view', 'create', 'edit', 'delete', 'approve', 'ban'] },
    { module: 'content', actions: ['view', 'create', 'edit', 'delete', 'approve'] },
    { module: 'reports', actions: ['view', 'create', 'edit', 'delete'] },
    { module: 'locations', actions: ['view', 'create', 'edit', 'delete', 'approve'] },
    { module: 'subscriptions', actions: ['view', 'create', 'edit', 'delete', 'configure'] },
    { module: 'analytics', actions: ['view', 'export'] },
    { module: 'settings', actions: ['view', 'edit', 'configure'] },
    { module: 'financial', actions: ['view', 'export', 'configure'] },
    { module: 'payments', actions: ['view', 'export', 'configure'] },
    { module: 'team_management', actions: ['view', 'create', 'edit', 'delete', 'invite', 'revoke'] },
    { module: 'system_admin', actions: ['view', 'edit', 'configure', 'emergency_access'] },
    { module: 'audit_logs', actions: ['view', 'export'] },
    { module: 'user_locations', actions: ['view', 'export'] }
  ],
  super_admin: [
    // Super admin has most permissions but cannot manage other super admins or owners
    { module: 'users', actions: ['view', 'create', 'edit', 'delete', 'approve', 'ban'] },
    { module: 'content', actions: ['view', 'create', 'edit', 'delete', 'approve'] },
    { module: 'reports', actions: ['view', 'create', 'edit', 'delete'] },
    { module: 'locations', actions: ['view', 'create', 'edit', 'delete', 'approve'] },
    { module: 'subscriptions', actions: ['view', 'create', 'edit', 'configure'] },
    { module: 'analytics', actions: ['view', 'export'] },
    { module: 'settings', actions: ['view', 'edit'] },
    { module: 'financial', actions: ['view', 'export'] },
    { module: 'payments', actions: ['view', 'export'] },
    { module: 'team_management', actions: ['view', 'create', 'edit', 'invite'], restrictions: { maxUsers: 10 } },
    { module: 'audit_logs', actions: ['view'] },
    { module: 'user_locations', actions: ['view', 'export'] }
  ],
  admin: [
    // Admin has operational permissions but limited system access
    { module: 'users', actions: ['view', 'edit', 'approve', 'ban'] },
    { module: 'content', actions: ['view', 'edit', 'approve'] },
    { module: 'reports', actions: ['view', 'edit'] },
    { module: 'locations', actions: ['view', 'edit', 'approve'] },
    { module: 'subscriptions', actions: ['view'] },
    { module: 'analytics', actions: ['view'] },
    { module: 'settings', actions: ['view'], restrictions: { readOnly: true } },
    { module: 'financial', actions: ['view'], restrictions: { readOnly: true } },
    { module: 'payments', actions: ['view'], restrictions: { readOnly: true } },
    { module: 'team_management', actions: ['view'], restrictions: { maxUsers: 3 } },
    { module: 'user_locations', actions: ['view'] }
  ],
  moderator: [
    // Moderator has limited permissions focused on content and user moderation
    { module: 'users', actions: ['view', 'ban'] },
    { module: 'content', actions: ['view', 'edit', 'approve'] },
    { module: 'reports', actions: ['view', 'edit'] },
    { module: 'locations', actions: ['view'] },
    { module: 'subscriptions', actions: ['view'], restrictions: { readOnly: true } },
    { module: 'analytics', actions: ['view'], restrictions: { readOnly: true } }
  ]
};

interface AdminContextType {
  currentAdmin: AdminUser | null;
  isAuthenticated: boolean;
  loading: boolean;
  login: (email: string, password?: string | null, googleUid?: string) => Promise<boolean>;
  logout: () => void;
  hasPermission: (module: AdminModule, action: AdminAction) => boolean;
  canManageUser: (targetUserRole: AdminUser['role']) => boolean;
  getEffectivePermissions: () => AdminPermission[];
  isOwner: () => boolean;
  isSuperAdmin: () => boolean;
  canAccessTeamManagement: () => boolean;
  getMaxManagedUsers: () => number;
}

const AdminContext = createContext<AdminContextType | undefined>(undefined);

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within AdminProvider');
  }
  return context;
};


interface AdminProviderProps {
  children: ReactNode;
}

export const AdminProvider: React.FC<AdminProviderProps> = ({ children }) => {
  const [currentAdmin, setCurrentAdmin] = useState<AdminUser | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  // Check admin authentication on mount
  useEffect(() => {
    const checkAuthState = () => {
      const user = auth.currentUser;
      if (user) {
        loadAdminData(user.uid, user.email!);
      } else {
        setLoading(false);
      }
    };

    const unsubscribe = auth.onAuthStateChanged(checkAuthState);
    return () => unsubscribe();
  }, []);

  const loadAdminData = async (uid: string, email: string) => {
    try {
      const adminDoc = await getDoc(doc(db, 'adminUsers', uid));
      if (adminDoc.exists()) {
        const data = adminDoc.data();
        const adminUser: AdminUser = {
          id: adminDoc.id,
          email,
          name: data.name,
          role: data.role,
          permissions: data.permissions || [],
          lastLogin: data.lastLogin?.toDate() || new Date(),
          isActive: data.isActive,
          created: data.created?.toDate() || new Date()
        };
        
        if (adminUser.isActive) {
          setCurrentAdmin(adminUser);
          setIsAuthenticated(true);

          // Set admin session in localStorage for protected routes
          localStorage.setItem('adminSession', JSON.stringify({
            uid: adminDoc.id,
            email: email,
            timestamp: Date.now()
          }));

          log('Admin user loaded:', adminUser.email);
        } else {
          logError('Admin account is inactive:', adminUser.email);
        }
      } else {
        logError('Admin user not found in collection:', email);
      }
    } catch (error) {
      logError('Error loading admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email: string, password?: string | null, googleUid?: string): Promise<boolean> => {
    try {
      console.log('🔐 Admin login attempt', { email, hasPassword: !!password, hasGoogleUid: !!googleUid });
      setLoading(true);

      let user;
      let userUid;

      if (googleUid) {
        // Google OAuth user - use the provided UID and email
        console.log('🔍 Checking Google OAuth admin permissions...');
        userUid = googleUid;
        user = { uid: googleUid, email };
      } else if (password) {
        // Email/password authentication
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        user = userCredential.user;
        userUid = user.uid;
        console.log('✅ Firebase auth successful, checking admin permissions...');
      } else {
        throw new Error('Either password or googleUid must be provided');
      }

      // Check if user is in admin collection
      const adminDoc = await getDoc(doc(db, 'adminUsers', userUid));
      if (!adminDoc.exists()) {
        if (!googleUid) {
          await signOut(auth);
        }
        console.error('❌ User is not in admin collection');
        logError('User is not an admin:', email);
        return false;
      }

      const adminData = adminDoc.data();
      console.log('📋 Admin data found');

      if (!adminData.isActive) {
        if (!googleUid) {
          await signOut(auth);
        }
        console.error('❌ Admin account is inactive');
        logError('Admin account is inactive:', email);
        return false;
      }

      console.log('✅ Admin login successful');

      // Set admin session in localStorage for protected routes
      localStorage.setItem('adminSession', JSON.stringify({
        uid: userUid,
        email: email,
        timestamp: Date.now(),
        authType: googleUid ? 'google' : 'email'
      }));

      // Admin data will be loaded by auth state change listener
      return true;
    } catch (error) {
      console.error('❌ Admin login failed:', error);
      logError('Admin login failed:', error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      setCurrentAdmin(null);
      setIsAuthenticated(false);

      // Clear admin session from localStorage
      localStorage.removeItem('adminSession');

      log('Admin logged out successfully');
    } catch (error) {
      logError('Admin logout failed:', error);
    }
  };

  const hasPermission = (
    module: AdminModule,
    action: AdminAction
  ): boolean => {
    if (!currentAdmin) return false;
    
    // Get effective permissions (role-based + custom)
    const effectivePermissions = getEffectivePermissions();
    const permission = effectivePermissions.find(p => p.module === module);
    
    if (!permission) return false;
    
    // Check if action is allowed
    const hasAction = permission.actions.includes(action);
    if (!hasAction) return false;
    
    // Apply restrictions if any
    if (permission.restrictions?.readOnly && ['create', 'edit', 'delete'].includes(action)) {
      return false;
    }
    
    return true;
  };
  
  const canManageUser = (targetUserRole: AdminUser['role']): boolean => {
    if (!currentAdmin) return false;
    
    // Role hierarchy: owner > super_admin > admin > moderator
    const roleHierarchy = ['owner', 'super_admin', 'admin', 'moderator'];
    const currentRoleIndex = roleHierarchy.indexOf(currentAdmin.role);
    const targetRoleIndex = roleHierarchy.indexOf(targetUserRole);
    
    // Can only manage users with lower roles
    return currentRoleIndex < targetRoleIndex;
  };
  
  const getEffectivePermissions = (): AdminPermission[] => {
    if (!currentAdmin) return [];
    
    // Start with role-based permissions
    const rolePermissions = ROLE_PERMISSIONS[currentAdmin.role] || [];
    
    // Merge with custom permissions (custom permissions can only restrict, not expand)
    if (currentAdmin.permissions && currentAdmin.permissions.length > 0) {
      // For now, return custom permissions if they exist
      // In future, we could implement proper merging logic
      return currentAdmin.permissions;
    }
    
    return rolePermissions;
  };
  
  const isOwner = (): boolean => {
    return currentAdmin?.role === 'owner';
  };
  
  const isSuperAdmin = (): boolean => {
    return currentAdmin?.role === 'super_admin' || isOwner();
  };
  
  const canAccessTeamManagement = (): boolean => {
    return hasPermission('team_management', 'view');
  };
  
  const getMaxManagedUsers = (): number => {
    if (!currentAdmin) return 0;
    
    const teamPermission = getEffectivePermissions().find(p => p.module === 'team_management');
    return teamPermission?.restrictions?.maxUsers || 0;
  };

  const value: AdminContextType = {
    currentAdmin,
    isAuthenticated,
    loading,
    login,
    logout,
    hasPermission,
    canManageUser,
    getEffectivePermissions,
    isOwner,
    isSuperAdmin,
    canAccessTeamManagement,
    getMaxManagedUsers
  };

  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  );
};

export default AdminContext;